import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { jwtDecode } from "jwt-decode";
import { environment } from "src/environments/environment";
import { AuthService } from "./../../shared/services/auth.service";
import Swal from "sweetalert2";

declare const google: any;

@Component({
  selector: "app-auth",
  templateUrl: "./auth.component.html",
  styleUrls: ["./auth.component.scss"],
})
export class AuthComponent implements OnInit, AfterViewInit, OnDestroy {
  redirectUrl = "/ecommerce/products";
  private shouldRenderGoogle = true;
  private googleInitialized = false;
  private googleInitAttempts = 0;
  private googleInitTimer: ReturnType<typeof setTimeout> | null = null;

  @ViewChild("googleButton", { static: false })
  googleButtonRef?: ElementRef<HTMLDivElement>;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.redirectUrl =
      this.route.snapshot.queryParamMap.get("redirect") ||
      "/ecommerce/products";

    const existingToken = this.authService.userToken;
    if (existingToken && !this.authService.isTokenExpired(existingToken)) {
      this.shouldRenderGoogle = false;
      this.router.navigateByUrl(this.redirectUrl);
    }
  }

  ngAfterViewInit(): void {
    if (!this.shouldRenderGoogle || this.googleInitialized || !this.googleButtonRef?.nativeElement) {
      return;
    }

    this.tryInitializeGoogle();
  }

  private tryInitializeGoogle(): void {
    if (!this.shouldRenderGoogle || this.googleInitialized || !this.googleButtonRef?.nativeElement) {
      return;
    }

    if (typeof google === "undefined" || !google?.accounts?.id) {
      this.googleInitAttempts += 1;

      if (this.googleInitAttempts >= 40) {
        console.error("Google Identity Services no terminó de cargar.");
        return;
      }

      this.googleInitTimer = setTimeout(() => this.tryInitializeGoogle(), 250);
      return;
    }

    this.googleInitialized = true;
    this.googleButtonRef.nativeElement.innerHTML = "";

    try {
      google.accounts.id.disableAutoSelect();
    } catch {
      // Ignore GIS auto-select reset issues.
    }

    google.accounts.id.initialize({
      client_id: environment.clientId,
      callback: this.handleCredentialResponse.bind(this),
      auto_select: false,
      cancel_on_tap_outside: false,
      itp_support: true,
      use_fedcm_for_prompt: true,
      context: "signin",
    });

    google.accounts.id.renderButton(this.googleButtonRef.nativeElement, {
      type: "standard",
      shape: "rectangular",
      theme: "outline",
      text: "continue_with",
      size: "large",
      width: 320,
    });

    google.accounts.id.prompt();
  }

  handleCredentialResponse(response: any) {
    const credential = response.credential;
    try {
      const googlePayload = jwtDecode<any>(credential);
      this.authService.cacheGoogleIdentity({
        name: googlePayload?.name,
        given_name: googlePayload?.given_name,
        email: googlePayload?.email,
        picture: googlePayload?.picture,
      });
    } catch {
      // Ignore Google payload decode issues and continue with backend login.
    }

    this.authService.loginWithGoogle(credential).subscribe({
      next: (result) => {
        if (!result?.isSuccess) {
          return;
        }

        this.router.navigateByUrl(this.redirectUrl);
      },
      error: (err) => {
        const message =
          err?.message ||
          err?.error?.message ||
          "No se pudo iniciar sesion con Google.";

        Swal.fire({
          title: "No pudimos iniciar sesion",
          text: message,
          icon: "warning",
          confirmButtonColor: "#9F59B1",
        });
      },
    });
  }

  ngOnDestroy(): void {
    this.shouldRenderGoogle = false;
    if (this.googleInitTimer) {
      clearTimeout(this.googleInitTimer);
      this.googleInitTimer = null;
    }

    try {
      google?.accounts?.id?.cancel();
    } catch {
      // Ignore cleanup issues from the GIS script during route changes.
    }
  }
}
